import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Share2, Check, Mail, Globe } from 'lucide-react';
import { PLANBASE_CONFIG } from '../config';
import { Button } from '../components/ui/button';

interface PortfolioProject {
  id: string;
  slug?: string;
  data: {
    title: string;
    description: string;
    category?: string;
    style?: string;
    location?: string;
    year?: string;
    architect?: string;
    coverImage?: string;
    images?: string[];
    tags?: string[];
    views?: number;
    likes?: number;
  };
  created_at: string;
}

interface UserProfile {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  bio?: string;
  avatar_url?: string;
  location?: string;
  website?: string;
  social_links?: {
    linkedin?: string;
    twitter?: string;
    instagram?: string;
  };
}

// Simple in-memory cache — persists across navigations within the session
const portfolioCache = new Map<string, { profile: UserProfile; projects: PortfolioProject[] }>();

export default function PublicPortfolio() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const fetchedRef = useRef(false);

  const portfolioUrl = `${window.location.origin}/portfolio/${username}`;

  useEffect(() => {
    if (!username) return;

    // Serve from cache instantly — no request needed
    const cached = portfolioCache.get(username);
    if (cached) {
      setProfile(cached.profile);
      setProjects(cached.projects);
      setLoading(false);
      return;
    }

    // Prevent StrictMode double-fetch
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const controller = new AbortController();

    const fetchPortfolio = async () => {
      try {
        setLoading(true);

        const profileResponse = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/planbase/users/profile/${username}`,
          {
            signal: controller.signal,
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!profileResponse.ok) throw new Error('Portfolio not found');

        const profileData = await profileResponse.json();
        const userData = profileData.data?.user || profileData.user;
        if (!userData) throw new Error('Invalid profile data');

        const projectsResponse = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/cms/records?database_slug=projects&status=published&created_by=${userData.id}`,
          {
            signal: controller.signal,
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'X-Project-ID': PLANBASE_CONFIG.projectId,
            },
          }
        );

        const fetchedProjects = projectsResponse.ok
          ? (await projectsResponse.json()).records || []
          : [];

        // Store in cache
        portfolioCache.set(username, { profile: userData, projects: fetchedProjects });

        setProfile(userData);
        setProjects(fetchedProjects);
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Failed to load portfolio');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
    return () => controller.abort();
  }, [username]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(portfolioUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <div className="flex items-start gap-6 mb-8">
              <div className="h-20 w-20 bg-gray-100 rounded-full animate-pulse" />
              <div className="flex-1 space-y-3">
                <div className="h-6 w-48 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
            <div className="space-y-2 mb-6">
              <div className="h-4 w-full max-w-2xl bg-gray-100 rounded animate-pulse" />
              <div className="h-4 w-3/4 max-w-xl bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3">
                <div className="aspect-[3/2] bg-gray-100 rounded-xl animate-pulse" />
                <div className="h-5 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Portfolio Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'This portfolio does not exist'}</p>
          <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const displayName = profile.full_name || profile.username;

  return (
    <div className="min-h-screen bg-white">
      {/* Profile Header */}
      <div className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-12">
          {/* Avatar & Name */}
          <div className="flex items-start gap-6 mb-8">
            <div className="h-20 w-20 rounded-full overflow-hidden bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0 shadow-sm">
              {profile.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt={displayName} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to initials if image fails to load
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.parentElement) {
                      e.currentTarget.parentElement.textContent = displayName.charAt(0).toUpperCase();
                    }
                  }}
                />
              ) : (
                displayName.charAt(0).toUpperCase()
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 mb-1">{displayName}</h1>
                  <p className="text-gray-500 text-sm">@{profile.username}</p>
                </div>
                
                {/* Share Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                >
                  {copied ? (
                    <><Check className="h-4 w-4 mr-1.5 text-green-600" /><span className="text-green-600">Copied</span></>
                  ) : (
                    <><Share2 className="h-4 w-4 mr-1.5" />Share</>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <p className="text-gray-700 text-base leading-relaxed mb-6 max-w-2xl">{profile.bio}</p>
          )}

          {/* Meta Info & Links */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {profile.location && (
              <span className="flex items-center gap-1.5 text-gray-600">
                <MapPin className="h-4 w-4" />
                {profile.location}
              </span>
            )}
            {profile.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-gray-600 hover:text-orange-600 transition-colors"
              >
                <Globe className="h-4 w-4" />
                Website
              </a>
            )}
            {profile.email && (
              <a
                href={`mailto:${profile.email}`}
                className="flex items-center gap-1.5 text-gray-600 hover:text-orange-600 transition-colors"
              >
                <Mail className="h-4 w-4" />
                Contact
              </a>
            )}
          </div>


        </div>
      </div>

      {/* Projects Section */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {projects.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500">No projects yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project) => (
              <div
                key={project.id}
                className="group cursor-pointer"
                onClick={() => navigate(`/project/${project.slug || project.id}`)}
              >
                {/* Project Image */}
                <div className="relative aspect-[3/2] overflow-hidden bg-gray-50 rounded-xl mb-3">
                  <img
                    src={project.data.coverImage || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop'}
                    alt={project.data.title}
                    className="w-full h-full object-cover group-hover:opacity-90 transition-opacity duration-200"
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
                    }}
                  />
                </div>

                {/* Project Info */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 group-hover:text-orange-600 transition-colors">
                    {project.data.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                    {project.data.description}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {project.data.category && (
                      <span className="capitalize">{project.data.category}</span>
                    )}
                    {project.data.year && <span>{project.data.year}</span>}
                    {project.data.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {project.data.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
